import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { 
  Search, Eye, Plus, FileText, Truck, Users, Calendar, 
  DollarSign, ChevronLeft, ChevronRight, Filter, RefreshCw,
  CheckCircle, Clock, PauseCircle, XCircle, AlertCircle, Trash2, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../../hooks/use-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { cn } from '../../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts de contrat
const CONTRACT_STATUS_CONFIG = {
  draft: { label: 'Brouillon', dotColor: 'bg-gray-400' },
  active: { label: 'Actif', dotColor: 'bg-green-500' },
  suspended: { label: 'Suspendu', dotColor: 'bg-amber-500' },
  closed: { label: 'Clôturé', dotColor: 'bg-gray-300' },
  cancelled: { label: 'Annulé', dotColor: 'bg-red-400' }
};

// Configuration des terminologies
const TERMINOLOGY_CONFIG = {
  transfert: { label: 'Transferts', plural: 'Transferts' },
  operation: { label: 'Opérations', plural: 'Opérations' },
  course: { label: 'Courses', plural: 'Courses' }
};

const ContractStatusBadge = ({ status }) => {
  const config = CONTRACT_STATUS_CONFIG[status] || CONTRACT_STATUS_CONFIG.draft;
  
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
      <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
};

const ContractsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canDeleteContracts = hasPermission('contracts.delete');
  
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  
  // KPI
  const [kpi, setKpi] = useState({
    total: 0,
    active: 0,
    draft: 0,
    suspended: 0
  });

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('skip', (currentPage - 1) * itemsPerPage);
      params.append('limit', itemsPerPage);
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await axios.get(`${API}/contracts?${params.toString()}`);
      
      setContracts(response.data.items || []);
      setTotalItems(response.data.total || 0);
      
      // Calculer les KPI - toujours charger tous les contrats pour les stats
      const allResponse = await axios.get(`${API}/contracts?limit=1000`);
      const allContracts = allResponse.data.items || [];
      
      setKpi({
        total: allContracts.length,
        active: allContracts.filter(c => c.status === 'active').length,
        draft: allContracts.filter(c => c.status === 'draft').length,
        suspended: allContracts.filter(c => c.status === 'suspended').length
      });
      
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les contrats",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchQuery]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  const formatPrice = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Sélection helpers — admins peuvent supprimer tous statuts
  const deletableContracts = user?.role === 'admin' ? contracts : contracts.filter(c => c.status === 'draft');
  const allDraftsSelected = deletableContracts.length > 0 && deletableContracts.every(c => selectedIds.includes(c.id));
  const someDraftsSelected = deletableContracts.some(c => selectedIds.includes(c.id));

  const handleSelectContract = (id, checked) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
  };

  const handleSelectAllDrafts = (checked) => {
    if (checked) {
      setSelectedIds(deletableContracts.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      const response = await axios.post(`${API}/contracts/bulk-delete`,
        { contract_ids: selectedIds },
        {}
      );
      const result = response.data;
      toast({
        title: "Suppression terminée",
        description: `${result.deleted} contrat(s) supprimé(s)${result.skipped > 0 ? `, ${result.skipped} ignoré(s)` : ''}`
      });
      setSelectedIds([]);
      setShowDeleteModal(false);
      fetchContracts();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Contrats de Location</h1>
          <p className="text-gray-500 mt-1">Gestion des contrats de location mensuelle</p>
        </div>
        <Button 
          onClick={() => navigate('/admin/contracts/new')}
          className="bg-red-600 hover:bg-red-700"
          data-testid="create-contract-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Contrat
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Contrats</p>
                <p className="text-2xl font-bold text-slate-900">{kpi.total}</p>
              </div>
              <div className="p-3 bg-slate-200/50 rounded-lg">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Actifs</p>
                <p className="text-2xl font-bold text-emerald-900">{kpi.active}</p>
              </div>
              <div className="p-3 bg-emerald-200/50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Brouillons</p>
                <p className="text-2xl font-bold text-gray-900">{kpi.draft}</p>
              </div>
              <div className="p-3 bg-gray-200/50 rounded-lg">
                <Clock className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Suspendus</p>
                <p className="text-2xl font-bold text-amber-900">{kpi.suspended}</p>
              </div>
              <div className="p-3 bg-amber-200/50 rounded-lg">
                <PauseCircle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher par N° contrat ou client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-contracts-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="suspended">Suspendus</SelectItem>
                <SelectItem value="closed">Clôturés</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchContracts} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Barre d'actions en masse */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-800">
            {selectedIds.length} brouillon{selectedIds.length > 1 ? 's' : ''} sélectionné{selectedIds.length > 1 ? 's' : ''}
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="ml-auto"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer la sélection
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedIds([])}
          >
            Annuler
          </Button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">Aucun contrat trouvé</p>
              <p className="text-sm">Créez votre premier contrat de location</p>
              <Button 
                onClick={() => navigate('/admin/contracts/new')}
                className="mt-4 bg-red-600 hover:bg-red-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un contrat
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  {canDeleteContracts && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allDraftsSelected}
                        onCheckedChange={handleSelectAllDrafts}
                        aria-label="Sélectionner tous les brouillons"
                        className={someDraftsSelected && !allDraftsSelected ? 'opacity-50' : ''}
                      />
                    </TableHead>
                  )}
                  <TableHead className="font-semibold">N° Contrat</TableHead>
                  <TableHead className="font-semibold">Client</TableHead>
                  <TableHead className="font-semibold">Période</TableHead>
                  <TableHead className="font-semibold">Terminologie</TableHead>
                  <TableHead className="font-semibold">Ressources</TableHead>
                  <TableHead className="font-semibold">Facturation</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow 
                    key={contract.id} 
                    className={cn(
                      "hover:bg-gray-50 cursor-pointer",
                      selectedIds.includes(contract.id) && "bg-red-50"
                    )}
                    onClick={() => navigate(`/admin/contracts/${contract.id}`)}
                    data-testid={`contract-row-${contract.id}`}
                  >
                    {canDeleteContracts && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(contract.id)}
                          onCheckedChange={(checked) => handleSelectContract(contract.id, checked)}
                          aria-label={`Sélectionner ${contract.contractNumber}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {contract.contractNumber || '-'}
                        </span>
                        {contract.hasSignedDocument && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            Signé
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-900">{contract.clientName}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(contract.startDate)} → {formatDate(contract.endDate)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-600">
                        {TERMINOLOGY_CONFIG[contract.terminology]?.label || contract.terminology}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Truck className="w-4 h-4" />
                          {contract.vehiclesCount || 0}
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <Users className="w-4 h-4" />
                          {contract.driversCount || 0}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="text-gray-600">
                          {contract.billingMode === 'monthly_fixed' 
                            ? 'Forfait' 
                            : contract.billingMode === 'fleet_location'
                            ? 'Location flotte' 
                            : 'À l\'unité'}
                        </span>
                        {(contract.billingMode === 'monthly_fixed' || contract.billingMode === 'fleet_location') && contract.monthlyValue && (
                          <div className="text-xs text-gray-400">
                            {new Intl.NumberFormat('fr-FR').format(contract.monthlyValue)} FCFA/mois
                          </div>
                        )}
                        {contract.billingMode !== 'monthly_fixed' && contract.billingMode !== 'fleet_location' && contract.pricePerUnit && (
                          <div className="text-xs text-gray-400">
                            {formatPrice(contract.pricePerUnit)}/{TERMINOLOGY_CONFIG[contract.terminology]?.label?.toLowerCase().replace(/s$/, '') || contract.terminology || 'unité'}
                          </div>
                        )}
                        {contract.billingMode !== 'monthly_fixed' && contract.billingMode !== 'fleet_location' && !contract.pricePerUnit && contract.monthlyValue && (
                          <div className="text-xs text-gray-400">
                            ~{formatPrice(contract.monthlyValue)}/mois (est.)
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ContractStatusBadge status={contract.status} />
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/admin/contracts/${contract.id}`)}
                          data-testid={`view-contract-${contract.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canDeleteContracts && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedIds([contract.id]);
                              setShowDeleteModal(true);
                            }}
                            title="Supprimer ce contrat"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-gray-500">
              Affichage {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>

    {/* Modale de confirmation de suppression */}
    <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Supprimer {selectedIds.length > 1 ? `${selectedIds.length} contrats` : 'ce contrat'}
          </DialogTitle>
          <DialogDescription>
            {selectedIds.length > 1
              ? `Vous êtes sur le point de supprimer ${selectedIds.length} contrats. Cette action est irréversible et supprimera toutes les données associées.`
              : 'Vous êtes sur le point de supprimer ce contrat. Cette action est irréversible et supprimera toutes les données associées.'
            }
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button variant="destructive" onClick={handleBulkDelete} disabled={deleting}>
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Supprimer définitivement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ContractsList;
